// src/pages/RequestLoader.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker } from '@react-google-maps/api';
import { FaCar, FaMapMarkerAlt, FaLocationArrow, FaClock, FaMoneyBillWave, FaSpinner } from 'react-icons/fa';
import API_URL from '../api/baseURL';

const containerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '400px',
  borderRadius: '0.5rem'
};

const defaultCenter = {
  lat: 5.3600, // Abidjan default
  lng: -4.0083
};

const libraries = ['places'];

const RequestLoader = () => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  // State
  const [step, setStep] = useState('input'); // input, confirming, searching, tracking
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');

  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  // Optional: who is expecting the car at the other end. Distinct from the
  // dropoff address — naming the workshop is what lets us warn them a tow is
  // inbound and tell them when it lands.
  const [workshops, setWorkshops] = useState([]);
  const [destinationWorkshop, setDestinationWorkshop] = useState('');
  // Exact coordinates for the chosen workshop, when it has them on file.
  // Routing to a LatLng beats geocoding a typed address — Abidjan addresses
  // resolve poorly, and "the wrong side of Cocody" is a real fare difference.
  const [destinationCoords, setDestinationCoords] = useState(null);

  // Picking a workshop fills in the destination, so the client does not have
  // to know their garage's street address. Everything stays editable after.
  const chooseWorkshop = (id) => {
    setDestinationWorkshop(id);
    const w = workshops.find((x) => x._id === id);
    setDestinationCoords(w?.location || null);
    // Only fill from a real address. Falling back to the business name would
    // hand Google a shop name to geocode and could route the truck somewhere
    // else entirely — worse than an empty field the client fills in.
    if (w?.address) setDropoff(w.address);
  };

  // Typing over the destination means the client is going somewhere other
  // than the workshop's address on file — drop the coordinates so the route
  // follows what they actually typed.
  const editDropoff = (value) => {
    setDropoff(value);
    setDestinationCoords(null);
  };

  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [price, setPrice] = useState(null);
  const [quote, setQuote] = useState(null); // server-computed, incl. breakdown

  const [activeRequestId, setActiveRequestId] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);
  const [assignedLoader, setAssignedLoader] = useState(null);
  const [loaderLocation, setLoaderLocation] = useState(null);

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Refs
  const mapRef = useRef(null);

  const onLoad = useCallback(function callback(map) {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(function callback(map) {
    mapRef.current = null;
  }, []);

  // Fetch user's vehicles
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await axios.get(`${API_URL}/vehicles/mine`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        });
        setVehicles(res.data);
      } catch (err) {
        console.error("Error fetching vehicles", err);
      }
    };
    fetchVehicles();
  }, []);

  // Workshops the client can name as the destination.
  useEffect(() => {
    axios.get(`${API_URL}/users/workshops`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
    })
      .then((res) => setWorkshops(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Error fetching workshops', err));
  }, []);

  // Calculate Route & Price
  const calculateRoute = async () => {
    if (!pickup || !dropoff) return;
    setError('');

    // Use Google Maps Directions Service
    /* eslint-disable no-undef */
    const directionsService = new google.maps.DirectionsService();
    try {
      const results = await directionsService.route({
        origin: pickup,
        // Coordinates when the workshop has them on file, the typed text
        // otherwise. DirectionsService accepts either.
        destination: destinationCoords || dropoff,
        travelMode: google.maps.TravelMode.DRIVING,
      });

      setDirectionsResponse(results);

      const route = results.routes[0].legs[0];
      const distValue = route.distance.value / 1000; // km
      const durText = route.duration.text;

      setDistance({ text: route.distance.text, value: distValue });
      setDuration({ text: durText, value: route.duration.value / 60 });

      // Price comes from the server. The fare is a multi-component calculation
      // (base + approach + distance, scaled by vehicle class and time of day),
      // so duplicating it here would guarantee the displayed price and the
      // charged price drift apart.
      const quoteRes = await axios.post(
        `${API_URL}/loader/quote`,
        {
          pickup: { lat: route.start_location.lat(), lng: route.start_location.lng() },
          dropoff: { lat: route.end_location.lat(), lng: route.end_location.lng() },
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );

      setQuote(quoteRes.data);
      setPrice(quoteRes.data.total);
      setStep('confirming');
    } catch (err) {
      console.error("Route/quote error:", err);
      // Never fall back to a locally guessed price — showing a number we
      // cannot honour is worse than showing none.
      setError(
        err.response
          ? "Could not price this trip. Please try again."
          : "Could not calculate route. Please check locations."
      );
    }
  };

  // Submit Request
  const submitRequest = async () => {
    if (!selectedVehicle) {
      setError("Please select a vehicle to transport.");
      return;
    }

    try {
      // Extract lat/lng from directions response if possible
      const route = directionsResponse.routes[0].legs[0];
      const pickupLoc = {
        address: route.start_address,
        lat: route.start_location.lat(),
        lng: route.start_location.lng()
      };
      const dropoffLoc = {
        address: route.end_address,
        lat: route.end_location.lat(),
        lng: route.end_location.lng()
      };

      const payload = {
        car: selectedVehicle,
        pickup: pickupLoc,
        dropoff: dropoffLoc,
        // Distance and price are recomputed server-side; quotedTotal lets the
        // backend reject the request if the price moved more than 10% since
        // the quote rather than silently charging a different amount.
        notes: "Request from web app",
        quotedTotal: quote?.total,
        destinationWorkshop: destinationWorkshop || undefined,
      };

      const res = await axios.post(`${API_URL}/loader/requests`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });

      setActiveRequestId(res.data.request._id);
      setStep('searching');
      setMessage("Request submitted! Finding nearby loaders...");
    } catch (err) {
      console.error(err);
      setError("Failed to create request.");
    }
  };

  // Poll for status updates
  useEffect(() => {
    let interval;
    if ((step === 'searching' || step === 'tracking') && activeRequestId) {
      interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API_URL}/loader/requests/${activeRequestId}/track`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
          });

          // The endpoint returns { request, loaderProfile }. Reading res.data
          // directly left status/assignedLoader undefined, so the condition
          // below could never be true and the view sat on "searching" forever
          // no matter what the loader did.
          const reqData = res.data?.request;
          if (!reqData) return;
          setRequestStatus(reqData.status);

          if (reqData.status !== 'searching' && reqData.assignedLoader) {
            setAssignedLoader(reqData.assignedLoader);
            if (reqData.loaderLocation) {
              setLoaderLocation(reqData.loaderLocation);
            }
            if (step === 'searching') setStep('tracking');
          }
        } catch (err) {
          console.error("Polling error", err);
        }
      }, 5000); // Poll every 5 seconds
    }
    return () => clearInterval(interval);
  }, [step, activeRequestId]);

  if (!isLoaded) return <div className="flex justify-center items-center h-64"><FaSpinner className="animate-spin text-4xl text-[#C9C3B6]" /></div>;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] lg:flex-row">
      {/* Sidebar / Panel */}
      <div className="w-full lg:w-1/3 bg-[#17140F] p-6 shadow-lg z-10 overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6 text-[#F4F1EA]">Request Loader</h2>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        {/* STEP 1: INPUT */}
        {step === 'input' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#C9C3B6] mb-1">Select Vehicle</label>
              <select
                value={selectedVehicle}
                onChange={(e) => setSelectedVehicle(e.target.value)}
                className="w-full border p-3 rounded bg-[#131110] focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="">-- Choose your vehicle --</option>
                {vehicles.map(v => (
                  <option key={v._id} value={v._id}>{v.brand} {v.model} - {v.plateNumber}</option>
                ))}
              </select>
            </div>

            <div className="relative">
              <FaLocationArrow className="absolute top-4 left-3 text-[#C9C3B6]" />
              <input
                type="text"
                placeholder="Pickup Location (e.g. Cocody)"
                value={pickup}
                onChange={e => setPickup(e.target.value)}
                className="w-full pl-10 p-3 border rounded bg-[#131110] focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="relative">
              <FaMapMarkerAlt className="absolute top-4 left-3 text-red-500" />
              <input
                type="text"
                placeholder="Dropoff Location (e.g. Yopougon)"
                value={dropoff}
                onChange={e => editDropoff(e.target.value)}
                className="w-full pl-10 p-3 border rounded bg-[#131110] focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="relative">
              <select
                value={destinationWorkshop}
                onChange={e => chooseWorkshop(e.target.value)}
                className="w-full p-3 border rounded bg-[#131110] focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Atelier de destination (optionnel)</option>
                {workshops.map(w => (
                  <option key={w._id} value={w._id}>
                    {w.businessName || w.details?.businessName || w.name}
                  </option>
                ))}
              </select>
              {destinationWorkshop && (
                <p className="mt-1 text-xs text-[#8F877B]">
                  {destinationCoords
                    ? "Destination remplie depuis la fiche de l'atelier."
                    : workshops.find(w => w._id === destinationWorkshop)?.address
                      ? "Adresse de l'atelier reprise ci-dessus — corrigez-la si besoin."
                      : "Cet atelier n'a pas d'adresse enregistrée : saisissez la destination vous-même."}
                  {' '}L'atelier sera prévenu par SMS dès qu'un remorqueur accepte, puis à la livraison.
                </p>
              )}
            </div>

            <button
              onClick={calculateRoute}
              disabled={!pickup || !dropoff || !selectedVehicle}
              className="w-full bg-black text-white py-3 rounded-lg font-semibold hover:bg-[#17140F] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Get Price Quote
            </button>
          </div>
        )}

        {/* STEP 2: CONFIRMING */}
        {step === 'confirming' && (
          <div className="space-y-6 fade-in">
            <div className="bg-[#131110] p-4 rounded-lg space-y-2">
              <div className="flex justify-between items-center text-[#C9C3B6]">
                <span className="flex items-center gap-2"><FaClock /> Duration</span>
                <span className="font-semibold">{duration?.text}</span>
              </div>
              <div className="flex justify-between items-center text-[#C9C3B6]">
                <span className="flex items-center gap-2"><FaLocationArrow /> Distance</span>
                <span className="font-semibold">{distance?.text}</span>
              </div>
              <div className="border-t pt-2 mt-2 flex justify-between items-center text-xl font-bold text-green-600">
                <span className="flex items-center gap-2"><FaMoneyBillWave /> Prix</span>
                <span>{price?.toLocaleString()} FCFA</span>
              </div>
              {quote?.breakdown && (
                <div className="text-xs text-[#6E665C] space-y-1 pt-1">
                  <div className="flex justify-between">
                    <span>Prise en charge</span>
                    <span>{quote.breakdown.base?.toLocaleString()} F</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Distance {quote.breakdown.roadKm} km × {quote.breakdown.ratePerKm}</span>
                    <span>{quote.breakdown.loaded?.toLocaleString()} F</span>
                  </div>
                  {quote.breakdown.approachFee > 0 && (
                    <div className="flex justify-between">
                      <span>Approche remorqueur</span>
                      <span>{quote.breakdown.approachFee?.toLocaleString()} F</span>
                    </div>
                  )}
                  {quote.breakdown.timeMultiplier !== 1 && (
                    <div className="flex justify-between">
                      <span>Tarif de nuit</span>
                      <span>×{quote.breakdown.timeMultiplier}</span>
                    </div>
                  )}
                  {quote.breakdown.minimumApplied && (
                    <div className="flex justify-between">
                      <span>Minimum appliqué</span>
                      <span>15 000 F</span>
                    </div>
                  )}
                  {quote.distance?.source === 'fallback' && (
                    <p className="text-amber-600 pt-1">
                      Itinéraire estimé — le prix final peut varier.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="border-l-4 border-blue-500 pl-4 py-1">
              <p className="text-sm text-[#8F877B]">Pickup</p>
              <p className="font-medium text-[#F4F1EA] truncate">{pickup}</p>
            </div>
            <div className="border-l-4 border-red-500 pl-4 py-1">
              <p className="text-sm text-[#8F877B]">Dropoff</p>
              <p className="font-medium text-[#F4F1EA] truncate">{dropoff}</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep('input')}
                className="flex-1 bg-[#241F1B] text-[#F4F1EA] py-3 rounded-lg font-semibold hover:bg-[#2B2724] transition"
              >
                Back
              </button>
              <button
                onClick={submitRequest}
                className="flex-1 bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] text-[#141210] text-white py-3 rounded-lg font-semibold hover:from-white hover:to-[#DED9CE] transition shadow-lg"
              >
                Confirm Request
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: SEARCHING */}
        {step === 'searching' && (
          <div className="flex flex-col items-center justify-center py-10 space-y-4">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-lg font-semibold text-[#C9C3B6]">Finding nearby loaders...</p>
            <p className="text-sm text-[#8F877B] text-center">We are notifying drivers in your area. Please wait.</p>
          </div>
        )}

        {/* STEP 4: TRACKING */}
        {step === 'tracking' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg flex items-center justify-between">
              <div>
                <h3 className="font-bold text-green-800 uppercase tracking-wider text-sm">{requestStatus?.replace('_', ' ')}</h3>
                <p className="text-sm text-green-700">Your loader is on the way.</p>
              </div>
              <div className="h-10 w-10 bg-green-200 rounded-full flex items-center justify-center text-green-700">
                <FaCar />
              </div>
            </div>

            {assignedLoader && (
              <div className="bg-[#17140F] border p-4 rounded-lg shadow-sm">
                <h4 className="font-semibold text-[#F4F1EA] mb-2">Driver Details</h4>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#2B2724] rounded-full overflow-hidden">
                    {/* Placeholder for avatar */}
                    <svg className="w-full h-full text-[#6E665C]" fill="currentColor" viewBox="0 0 24 24"><path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                  </div>
                  <div>
                    <p className="font-bold">{assignedLoader.name || 'Loader'}</p>
                    <p className="text-sm text-[#8F877B]">{assignedLoader.phone || 'No phone'}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <button
                className="w-full bg-[#1B1815] text-[#C9C3B6] py-2 rounded font-medium hover:bg-[#241F1B]"
                onClick={() => window.location.reload()}
              >
                Create New Request
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Map Container */}
      <div className="w-full lg:w-2/3 h-64 lg:h-full relative">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={defaultCenter}
          zoom={12}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: true,
          }}
        >
          {directionsResponse && (
            <DirectionsRenderer
              directions={directionsResponse}
              options={{
                polylineOptions: {
                  strokeColor: '#2563EB',
                  strokeWeight: 5,
                }
              }}
            />
          )}

          {/* Loader real-time position marker */}
          {loaderLocation && (
            <Marker
              position={loaderLocation}
              icon={{
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 6,
                fillColor: "#000000",
                fillOpacity: 1,
                strokeWeight: 2,
                rotation: 0 // In a real app we would calculate bearing
              }}
            />
          )}
        </GoogleMap>
      </div>
    </div>
  );
};

export default RequestLoader;
